import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  CheckCircle, 
  Building2, 
  Users, 
  Star, 
  Shield, 
  Target, 
  ArrowRight,
  Calendar,
  MapPin,
  Award,
  Zap,
  TrendingUp,
  Globe2,
  Languages
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '@/styles/animations.css';

const TranslationServices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      title: "Certified Translation",
      description: "Official translations accepted by Polish authorities",
      icon: Shield
    },
    {
      title: "Multiple Languages",
      description: "Support for 50+ languages and dialects",
      icon: Languages
    },
    {
      title: "Fast Turnaround",
      description: "Quick delivery without compromising quality",
      icon: Zap
    },
    {
      title: "Expert Translators",
      description: "Native speakers with specialized expertise",
      icon: Users
    }
  ];

  const requirements = [
    "Original document in good condition",
    "Clear specifications of target language",
    "Purpose of translation (legal, business, personal)",
    "Urgency level and deadline requirements",
    "Any specific formatting requirements",
    "Certification needs (if required)",
    "Contact information for delivery",
    "Payment method preference"
  ];

  const process = [
    {
      step: "1",
      title: "Document Submission",
      description: "Submit your documents and requirements"
    },
    {
      step: "2",
      title: "Assessment",
      description: "Review and quote for translation services"
    },
    {
      step: "3",
      title: "Translation",
      description: "Professional translation by certified experts"
    },
    {
      step: "4",
      title: "Quality Check",
      description: "Review and proofreading for accuracy"
    },
    {
      step: "5",
      title: "Delivery",
      description: "Deliver translated documents with certification"
    }
  ];

  const benefits = [
    "Official acceptance by Polish authorities",
    "Accurate and professional translations",
    "Fast turnaround times",
    "Competitive pricing",
    "Certified and notarized documents",
    "Multiple language support",
    "Confidentiality guaranteed",
    "Quality assurance process",
    "Express service available",
    "Ongoing support and revisions"
  ];

  const documentTypes = [
    "Birth certificates and marriage certificates",
    "Educational diplomas and transcripts",
    "Legal documents and contracts",
    "Business documents and certificates",
    "Medical records and prescriptions",
    "Immigration documents",
    "Financial statements and bank documents",
    "Technical manuals and specifications",
    "Marketing materials and websites",
    "Personal correspondence and letters"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-pink-500/5 via-pink-600/10 to-background py-24 sm:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))] dark:bg-grid-black/10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-pink-500/10 text-pink-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileText className="h-4 w-4" />
              Translation Services
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-6 font-alumni animate-fade-in">
              Professional Document Translation
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind animate-subtle-slide-up mb-8">
              Professional document translation for all your immigration needs. 
              We work with certified translators to ensure all your documents 
              meet official requirements and are accepted by Polish authorities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-pink-600 hover:bg-pink-700 text-white">
                  Get Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Features Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Key Benefits</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              What you get with our Translation Services
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <CardHeader className="pb-4">
                    <div className="mx-auto bg-pink-500/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-pink-600" />
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground font-alumni">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground font-hind">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6 font-alumni">Requirements</h2>
              <p className="text-lg text-muted-foreground mb-8 font-hind">
                To get started with our translation services, we need the following information:
              </p>
              <div className="space-y-4">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-pink-600 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground font-hind">{requirement}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-pink-50 rounded-2xl p-8">
              <div className="text-center">
                <div className="bg-pink-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Target className="h-8 w-8 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 font-alumni">Turnaround Time</h3>
                <p className="text-3xl font-bold text-pink-600 mb-2">1-3 Days</p>
                <p className="text-muted-foreground font-hind">Standard delivery time for most documents</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Translation Process</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Our streamlined 5-step process ensures accurate and timely translations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-pink-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-pink-600">{step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 font-alumni">{step.title}</h3>
                <p className="text-muted-foreground text-sm font-hind">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Document Types Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-pink-500/5 to-pink-600/10 rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Document Types</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
                We translate a wide variety of documents for immigration and business purposes
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {documentTypes.map((docType, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-pink-600 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground font-hind">{docType}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Why Choose Our Translation Services?</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Professional, reliable, and certified translation services
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-pink-600 mt-0.5 flex-shrink-0" />
                <span className="text-foreground font-hind">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-pink-600 to-pink-700 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4 font-alumni">Need Professional Translation?</h2>
            <p className="text-xl mb-8 opacity-90 font-hind">
              Get your documents translated quickly and accurately by certified professionals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-pink-600 hover:bg-gray-100">
                  Get Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white !text-pink-400 hover:bg-white/10 hover:!text-white"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TranslationServices; 